import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  useTheme, 
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { 
  CreditCard as CreditCardIcon,
  History as HistoryIcon,
  CardMembership as CardMembershipIcon
} from '@mui/icons-material';
import config from 'config';

// Import tab components
import MyPlanTab from './Components/MyPlanTab';
import HistoryTab from './Components/HistoryTab';
import SavedCardsTab from './Components/SavedCardsTab';

const Subscriptions = React.memo(() => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1020px)');
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get user data
  const { _id, user: userId } = useSelector((state) => state.profile.user);
  const { isGuest } = useSelector((state) => state.main);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [hasPaid, setHasPaid] = useState(false);
  const [loading, setLoading] = useState(true);

  // Tab configuration
  const tabs = useMemo(() => [
    {
      label: 'My Plan',
      icon: <CreditCardIcon />,
      component: MyPlanTab,
      requiresPayment: true
    },
    {
      label: 'History',
      icon: <HistoryIcon />,
      component: HistoryTab,
      requiresPayment: true
    },
    {
      label: 'Saved Cards',
      icon: <CardMembershipIcon />,
      component: SavedCardsTab,
      requiresPayment: false
    }
  ], []);

  // Check subscription status
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const { createAxiosInstance } = await import('../../api');
        const { API_URL } = await import('../../api');
        
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(`${API_URL}/api/billings/status/${userId}`);
        
        const { status } = response.data || {};
        const normalizedStatus = status || 'inactive';
        setSubscriptionStatus(normalizedStatus);
        
        // Check if user has paid (active subscription)
        const paidStatuses = ['active', 'paused', 'past_due', 'canceled'];
        setHasPaid(paidStatuses.includes(normalizedStatus));
        
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setSubscriptionStatus('inactive');
        setHasPaid(false);
      } finally {
        setLoading(false);
      }
    };

    checkSubscriptionStatus();
  }, [userId]);

  // Handle tab change
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  // Get current tab component
  const CurrentTabComponent = tabs[activeTab]?.component;

  // Check if current tab requires payment
  const currentTabRequiresPayment = tabs[activeTab]?.requiresPayment;
  const canAccessCurrentTab = !currentTabRequiresPayment || hasPaid;

  // Redirect to dashboard if no profile (but wait for loading to complete)
  useEffect(() => {
    if (!loading && !_id) {
      navigate('/dashboard');
      return;
    }
  }, [loading, _id, navigate]);

  // Auto-switch to Saved Cards tab if user doesn't have paid subscription and tries to access paid features
  useEffect(() => {
    if (!loading && currentTabRequiresPayment && !hasPaid) {
      setActiveTab(2); // Switch to Saved Cards tab (doesn't require payment)
    }
  }, [loading, currentTabRequiresPayment, hasPaid]);

  if (!loading && !_id) {
    return null; // Will redirect to dashboard
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 2, 
      maxWidth: '100%'
    }}>
      {/* Page Title */}
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
        Subscriptions
      </Typography>

      {/* Horizontal Tabs */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: 'white',
        borderRadius: 1,
        px: 2
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
              height: 3
            },
            '& .MuiTab-root': {
              minHeight: '48px',
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-selected': {
                color: theme.palette.primary.main
              }
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              disabled={tab.requiresPayment && !hasPaid}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.5,
                  color: 'text.secondary'
                }
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Content Area */}
      <Box sx={{ 
        backgroundColor: 'white',
        borderRadius: 1,
        minHeight: '400px'
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '400px',
            gap: 2
          }}>
            <CircularProgress size={40} />
            <Typography variant="body1" color="text.secondary">
              Loading subscription information...
            </Typography>
          </Box>
        ) : canAccessCurrentTab && CurrentTabComponent ? (
          <CurrentTabComponent 
            subscriptionStatus={subscriptionStatus}
            hasPaid={hasPaid}
            userId={userId}
            isActive={true}
          />
        ) : (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            height: '400px',
            gap: 2,
            p: 3
          }}>
            <CreditCardIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            <Typography variant="h6" color="text.secondary">
              Subscription Required
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              You need an active subscription to access this feature.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
});

Subscriptions.displayName = 'Subscriptions';

export default Subscriptions;
